import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shield, Users, DollarSign, TrendingUp, Activity, LogOut, Moon, Sun, Plus, Minus, Trash2, LayoutDashboard } from "lucide-react";

const StatBox = ({ Icon, label, value, accent }) => (
  <div className="p-5 rounded-2xl border border-border bg-card" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-3`}><Icon size={18} className="text-white" /></div>
    <p className="font-display text-2xl font-bold">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export default function Admin() {
  const { logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/audit").then((r) => setLogs(r.data.logs)).catch(() => {});
  };
  useEffect(load, []);

  const adjust = async (uid, delta) => { await api.patch(`/admin/users/${uid}/credits`, { credits: delta }); load(); };
  const del = async (uid) => { if (!window.confirm("Supprimer cet utilisateur ?")) return; await api.delete(`/admin/users/${uid}`); toast.success("Utilisateur supprimé"); load(); };

  return (
    <div className="min-h-screen bg-background mg-radial">
      <header className="h-16 border-b border-border mg-glass px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3"><Logo size="sm" /><span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-500 text-xs font-semibold flex items-center gap-1"><Shield size={12} /> Super Admin</span></div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="rounded-lg" data-testid="admin-goto-app"><LayoutDashboard size={16} className="mr-1.5" /> App</Button>
          <button onClick={toggle} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-secondary">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
          <button onClick={() => { logout(); navigate("/"); }} className="h-9 w-9 flex items-center justify-center rounded-lg text-destructive hover:bg-destructive/10" data-testid="admin-logout"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
        <h1 className="font-display text-3xl font-bold">Panneau d'administration</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatBox Icon={Users} label="Utilisateurs" value={stats?.total_users ?? "—"} accent="from-indigo-500 to-violet-500" />
          <StatBox Icon={DollarSign} label="MRR" value={`${(stats?.mrr ?? 0).toLocaleString("fr-FR")} €`} accent="from-emerald-400 to-cyan-500" />
          <StatBox Icon={TrendingUp} label="ARR" value={`${(stats?.arr ?? 0).toLocaleString("fr-FR")} €`} accent="from-cyan-500 to-blue-500" />
          <StatBox Icon={DollarSign} label="Revenus encaissés" value={`${(stats?.revenue ?? 0).toLocaleString("fr-FR")} €`} accent="from-pink-500 to-rose-500" />
          <StatBox Icon={Activity} label="Campagnes" value={stats?.total_campaigns ?? "—"} accent="from-amber-400 to-orange-500" />
        </div>

        <Tabs defaultValue="users">
          <TabsList className="rounded-xl"><TabsTrigger value="users" className="rounded-lg" data-testid="admin-tab-users">Utilisateurs</TabsTrigger><TabsTrigger value="audit" className="rounded-lg" data-testid="admin-tab-audit">Journal d'activités</TabsTrigger></TabsList>

          <TabsContent value="users" className="mt-5">
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="p-4">Marque</th><th className="p-4">E-mail</th><th className="p-4">Plan</th><th className="p-4">Crédits</th><th className="p-4">Rôle</th><th className="p-4 text-right">Actions</th></tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-border" data-testid={`admin-user-${u.user_id}`}>
                        <td className="p-4 font-medium">{u.name}</td>
                        <td className="p-4 text-muted-foreground">{u.email}</td>
                        <td className="p-4"><span className="px-2 py-0.5 rounded-md bg-secondary text-xs capitalize">{u.plan}</span></td>
                        <td className="p-4 font-mono">{u.credits?.toLocaleString("fr-FR")}</td>
                        <td className="p-4">{u.role === "admin" ? <span className="text-amber-500 text-xs font-semibold">Admin</span> : <span className="text-muted-foreground text-xs">Client</span>}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => adjust(u.user_id, 100)} className="h-7 w-7 rounded-lg hover:bg-emerald-500/10 text-emerald-500 flex items-center justify-center" title="+100 crédits" data-testid={`credit-add-${u.user_id}`}><Plus size={14} /></button>
                            <button onClick={() => adjust(u.user_id, -100)} className="h-7 w-7 rounded-lg hover:bg-amber-500/10 text-amber-500 flex items-center justify-center" title="-100 crédits"><Minus size={14} /></button>
                            {u.role !== "admin" && <button onClick={() => del(u.user_id)} className="h-7 w-7 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center" data-testid={`user-delete-${u.user_id}`}><Trash2 size={14} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-5">
            <div className="rounded-2xl border border-border bg-card p-4 space-y-1 max-h-[500px] overflow-y-auto">
              {logs.map((l, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-secondary/50 text-sm" data-testid={`audit-log-${i}`}>
                  <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
                  <span className="font-mono text-xs text-muted-foreground w-36 shrink-0">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                  <span className="font-medium">{l.action}</span>
                  <span className="text-muted-foreground truncate">{l.detail}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
